import { BallType } from 'lib_ts/enums/machine.enums';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { IOnDemandMachineState } from 'lib_ts/interfaces/on-demand/i-machine-state';
import { IOnDemandSnapshot } from 'lib_ts/interfaces/on-demand/i-snapshot';
import { OnDemandService } from 'services/on-demand.service';
import { create } from 'zustand';

const CONTEXT_NAME = 'useOnDemandMsStore';

interface State {
  snapshot: IOnDemandSnapshot | undefined;
  // if a rebuild/reset is in progress, TDS can report the progress
  snapshotProgress: number | undefined;
  // from when buildTmsDict was last called
  last_hardware_changed: string | undefined;
  // key is bs_hash, starts as empty and only gets populated as the user selects pitches in on-demand
  odmsDict: DictOf<IOnDemandMachineState>;
}

interface Actions {
  // only updates local stuff and doesn't write anything to the database
  mixin: (value: IOnDemandMachineState) => void;

  // writes changes to the database and then updates the tmsDict
  upsert: (
    value: Partial<IOnDemandMachineState> & {
      machineID: string; // required for upsert lookup
      ball_type: BallType; // required for upsert lookup
      bs_hash: string; // required for upsert lookup
      deleted: boolean; // required for readability
    }
  ) => Promise<IOnDemandMachineState | undefined>;
}

export const useOnDemandStore = create<State & Actions>((set, get) => ({
  snapshot: undefined,
  snapshotProgress: undefined,
  last_hardware_changed: undefined,
  odmsDict: {},

  mixin: (value) => {
    set((state) => ({
      // only the changed key gets a new reference; all other entries keep
      // referential identity so per-key selectors don't notify subscribers
      odmsDict: { ...state.odmsDict, [value.bs_hash]: value },
    }));
  },

  upsert: async (value) => {
    const odms = await OnDemandService.getInstance().upsertOne(value);

    if (!odms) {
      console.warn(`${CONTEXT_NAME}: failed to upsert ODMS`, value);
      return undefined;
    }

    set((state) => ({
      // only the changed key gets a new reference; all other entries keep
      // referential identity so per-key selectors don't notify subscribers
      odmsDict: { ...state.odmsDict, [odms.bs_hash]: odms },
    }));

    return odms;
  },
}));
